/**
 * Handle all our SEO and Google Tag Manager stuff
 *
 */
import slugify from 'slugify';

const debug = document.body.dataset.analyticsDemo;

function sendDataToGtm(data) {
  if (parseInt(debug, 10) === 1) {
    console.log(data); // eslint-disable-line
  } else {
    window.dataLayer.push(data);
  }
}

class SEO {
  constructor() {
    this.iframes = [...document.querySelectorAll('iframe[src*="youtube.com"]')];
    this.searchResultContainer = document.querySelector('.search-results-container');
    this.contactForm = document.querySelector('.js-contact-form');

    if (this.iframes && this.iframes.length) this.youtubeIframe();
    if (this.searchResultContainer) this.searchResults();
    if (this.contactForm) this.contactFormEvent();
  }

  sendDataToGtm(data) {
    if (parseInt(this.debug, 10) === 1) {
      console.log(data); // eslint-disable-line
    } else {
      window.dataLayer.push(data);
    }
  }

  /**
   * Add parameter to YouTube iframes src attribute.
   * This works for all youtube videos with `youtube.com` url.
   * @see videoToEmbedUrl() in `app/Support/helpers.php` for default videos.
   */
  youtubeIframe() {
    const parameter = 'enablejsapi=1';

    this.iframes.forEach((iframe) => {
      iframe.src = `${iframe.src}?${parameter}`;
    });
  }

  /**
   * Search Results page
  */
  searchResults() {
    const loc = window.location.toString();
    const urlKeyword = decodeURIComponent((loc.split('?q=')[1]).replace(/\+/g, '%20'));
    const pageKeyword = document.querySelector('.js-search-query').innerHTML;
    const nbResults = document.querySelector('.results-number');
    const searchResults = [...document.querySelectorAll('.listing-item-container a')];

    // Send the GTM event when we have search results
    if (urlKeyword || pageKeyword) {
      setTimeout(() => {
        sendDataToGtm({
          event: 'recherche',
          terms: urlKeyword || pageKeyword,
          nbResult: nbResults ? nbResults.dataset.results : '0',
          'type-recherche': 'Recherche manuelle',
        });
      }, 600);
    }

    // Send the GTM event when clicking on a result
    if (searchResults && searchResults.length) {
      searchResults.forEach((result) => {
        result.addEventListener('click', (e) => {
          const elemTitle = result.querySelector('.title').dataset.title;
          const elemType = result.querySelector('.type').dataset.type;

          // Prevent url redirect so we have enough time to send the event.
          e.preventDefault();

          sendDataToGtm({
            event: 'clic-resultats',
            nom: elemTitle,
            typologie: elemType,
            'type-recherche': 'Recherche manuelle',
          });

          // Now that the event is sent, we can redirect.
          setTimeout(() => {
            document.location = result.dataset.url;
          }, 100);
        });
      });
    }
  }

  /**
   * Send the GTM event on contact form submit.
   */
  contactFormEvent() {
    const selectInput = this.contactForm.querySelector('.js-type');
    const sendButton = this.contactForm.querySelector('.js-send-btn');
    this.value = selectInput.options[selectInput.selectedIndex].text;
    this.isValid = false;

    // Store the select element text (not the value).
    selectInput.addEventListener('change', () => {
      this.value = selectInput.options[selectInput.selectedIndex].text;
    });

    // Send the GTM event on form submit.
    sendButton.addEventListener('click', () => {
      // Check if all required inputs are filled.
      this.checkRequiredInputs();

      if (this.isValid) {
        sendDataToGtm({
          event: 'formulaire',
          profil: this.value,
        });
      }
    });
  }

  checkRequiredInputs() {
    const requiredInputs = this.contactForm.querySelectorAll('[required]');

    requiredInputs.forEach((input) => {
      if (!input.value) {
        this.isValid = false;
      } else {
        this.isValid = true;
      }
    });
  }
}

/**
 * Send a GTM event when clicking on a "autosuggested" search result.
 *
 * @param {string} term the suggested product/ingredient/patent name.
 * @param {element} item the suggested search item.
 * @uses SearchSuggestJS
 */
function searchSuggestion(term, item) {
  sendDataToGtm({
    event: 'recherche',
    nom: term,
    typologie: item.querySelector('.type').dataset.type,
    'type-recherche': 'Recherche suggérée',
  });
}

/**
 * Send a GTM event when clicking on a dataviz bubble.
 *
 * @param {string} type the bubble type (patent/ingredient)
 * @param {string} role the bubble name.
 */
function gtmBubbles(type, role) {
  const loc = window.location.pathname.toString();
  const productTitle = document.querySelector('.product-title').dataset.title;

  if (type === 'ingredient') {
    sendDataToGtm({
      event: 'VirtualPageview',
      role,
      'type-page': 'Dataviz-Listing-Ingredient',
      itemtype: 'ingredient',
      virtualPageURL: `${loc}/dataviz/${slugify(role).toLowerCase()}.html`,
      virtualPageTitle: `${role} - ${productTitle}`,
    });
  } else if (type === 'brevet') {
    sendDataToGtm({
      event: 'VirtualPageview',
      role,
      'type-page': 'Dataviz-Brevet',
      itemtype: 'brevet',
      virtualPageURL: `${loc}/dataviz/brevet/${slugify(role).toLowerCase()}.html`,
      virtualPageTitle: `${role} - ${productTitle}`,
    });
  }
}

function gtmBubbleIng(name) {
  const loc = window.location.pathname.toString();
  const productTitle = document.querySelector('.product-title').dataset.title;

  sendDataToGtm({
    event: 'VirtualPageview',
    'type-page': 'Dataviz-Ingredient',
    itemtype: 'ingredient',
    nom: name,
    virtualPageURL: `${loc}/dataviz/ingredient/${slugify(name).toLowerCase()}.html`,
    virtualPageTitle: `${name} - ${productTitle}`,
  });
}

/**
 * Send a GTM event when clicking on an ingredient in the Inci list.
 *
 * @param {string} ingredientName the ingredient name
 */
function gtmInci(ingredientName) {
  const loc = window.location.pathname.toString();
  const productTitle = document.querySelector('.product-title').dataset.title;

  sendDataToGtm({
    event: 'VirtualPageview',
    'type-page': 'INCI',
    itemtype: 'ingredient',
    nom: ingredientName,
    virtualPageURL: `${loc}/inci/ingredient/${slugify(ingredientName).toLowerCase()}.html`,
    virtualPageTitle: `${ingredientName} - ${productTitle}`,
  });
}

/**
 * Send a GTM event when switching view in a product page.
 *
 * @param {string} display the full current view name.
 * @param {string} displayVirtual the short current view name with .html at the end
 */
function gtmDatavizSwitcher(display, displayVirtual) {
  const loc = window.location.pathname.toString();
  const productTitle = document.querySelector('.product-title').dataset.title;

  sendDataToGtm({
    event: 'switch-vue',
    vue: display,
    virtualPageURL: `${loc}/${displayVirtual}`,
    virtualPageTitle: `${display} - ${productTitle}`,
  });
}

/**
 * Send a GTM event when using one of the product page family filter.
 *
 * @param {string} currentView the current view (dataviz or inci)
 * @param {string} currentFilter the used filter
 */
function gtmFilters(currentView, currentFilter) {
  sendDataToGtm({
    event: 'Filtre',
    'type-page': currentView,
    filtre: currentFilter,
  });
}

export { searchSuggestion, SEO, gtmBubbles, gtmInci, gtmBubbleIng, gtmDatavizSwitcher, gtmFilters };
